import { ArrowLeft, Check, GraduationCap, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { useSession } from '@/stores/session';
import { AddLessonForm } from '../components/AddLessonForm';
import { canManageLms, lessonKindLabel } from '../constants';
import {
  useCompleteLesson,
  useCourse,
  useDeleteCourse,
  useEnroll,
  useLessons,
  useMyEnrollments,
} from '../queries';

export function CourseDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const role = useSession((s) => s.role);
  const canManage = canManageLms(role);

  const { data: course, isPending, isError, error } = useCourse(publicId);
  const courseId = course?.id ?? -1;
  const lessons = useLessons(courseId);
  const enrollments = useMyEnrollments();
  const del = useDeleteCourse();
  const enroll = useEnroll();

  const myEnrollment = enrollments.data?.results.find((e) => e.course === courseId);
  const complete = useCompleteLesson(myEnrollment?.id ?? -1);

  async function handleDelete() {
    if (!course) return;
    await del.mutateAsync(course.id);
    navigate('/lms/courses');
  }

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/lms/courses')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const lessonRows = lessons.data?.results ?? [];
  const nextOrder = lessonRows.reduce((max, l) => Math.max(max, l.order), -1) + 1;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/lms/courses')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{course.title}</h1>
          <Badge variant={course.is_published ? 'default' : 'secondary'}>
            {course.is_published ? 'Published' : 'Draft'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {myEnrollment ? (
            <Badge variant="outline">{Number(myEnrollment.progress_percent)}% complete</Badge>
          ) : (
            <Button onClick={() => enroll.mutate(courseId)} disabled={enroll.isPending}>
              <GraduationCap /> {enroll.isPending ? 'Enrolling…' : 'Enroll'}
            </Button>
          )}
          {canManage ? (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/lms/courses/${course.public_id}/edit`)}
              >
                <Pencil /> Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {course.title}?</AlertDialogTitle>
                    <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} disabled={del.isPending}>
                      {del.isPending ? 'Deleting…' : 'Delete'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : null}
        </div>
      </div>

      {course.description ? (
        <Card>
          <CardContent className="text-muted-foreground py-4 text-sm whitespace-pre-wrap">
            {course.description}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Lessons</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden sm:table-cell">Kind</TableHead>
                  {myEnrollment ? <TableHead className="text-right">Action</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lessons.isPending ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-6">
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                  </TableRow>
                ) : lessonRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground py-8 text-center">
                      No lessons yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  lessonRows
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map((l) => (
                      <TableRow key={l.id}>
                        <TableCell className="text-muted-foreground">{l.order}</TableCell>
                        <TableCell className="font-medium">{l.title}</TableCell>
                        <TableCell className="text-muted-foreground hidden sm:table-cell">
                          {lessonKindLabel(l.kind)}
                        </TableCell>
                        {myEnrollment ? (
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => complete.mutate(l.id)}
                              disabled={complete.isPending}
                            >
                              <Check /> Complete
                            </Button>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </div>

          {canManage ? <AddLessonForm courseId={course.id} nextOrder={nextOrder} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
